import { ChatCitationType, EngagementType, InteractionTypeEnum } from '@trinity/chat';;
import type { Chat } from '@trinity/chat';
import type { KolSearchResultSortKey, FilterType, FilterEntry } from './interfaces.types';

export const SORT_KEYS: KolSearchResultSortKey[] = ['affiliation', 'email', 'location', 'name', 'firstName', 'lastName', 'phone', 'isMun', 'distance'];
export const FILTER_TYPES: FilterType[] = ['contains', 'does-not-contain', 'equals', 'does-not-equal', 'starts-with', 'ends-with', 'is-empty', 'is-not-empty', 'is within'];

export function isFilterValid<T extends string>(filter: FilterEntry<T>) {
  if (!filter.key || !filter.type) return false;

  if (!doesFilterTypeRequireValue(filter.type)) return true;

  if (filter.type === 'is within') {
    return isDistanceFilterValid(filter as FilterEntry<'is within'>);
  }

  return !!filter.value?.trim();
}

function isDistanceFilterValid(filter: FilterEntry<'is within'>) {
  const value = Number(filter.value);
  return !isNaN(value) && value > 0;
}

export function doesFilterTypeRequireValue(type: FilterType) {
  const types: FilterType[] = ['is-empty', 'is-not-empty'];
  return !types.includes(type);
}

export function areFiltersSimilar(mainKey: KolSearchResultSortKey, innerKey: KolSearchResultSortKey) {
  if (mainKey === innerKey) return true;

  return mainKey === 'name' &&
    (innerKey === 'firstName' || innerKey === 'lastName');
}

export function getValidFilterTypesForKey(key: KolSearchResultSortKey): FilterType[] {
  if (key === 'isMun') {
    return ['equals', 'does-not-equal', 'is-empty', 'is-not-empty'];
  }

  if (key === 'distance') {
    return ['is within'];
  }

  return FILTER_TYPES;
}

export function getDefaultTypeForKey(key: KolSearchResultSortKey) {
  return getValidFilterTypesForKey(key)[0];
}

export function getEngagementType(citation: Chat.Citation): EngagementType[] {

  const proactiveSources = [
    ChatCitationType.ScientificLeaderTopic, ChatCitationType.ClinicalLeaderTopic,
    ChatCitationType.DigitalLeaderTopic, ChatCitationType.Association, ChatCitationType.Affiliation,
    ChatCitationType.Collaboration, ChatCitationType.CompanySponsorship, ChatCitationType.ClinicalTrial,
    ChatCitationType.Conference, ChatCitationType.Publication, ChatCitationType.CongressAbstract,
    ChatCitationType.ContactDetail, ChatCitationType.UnmetNeed,
  ];

  const reactiveSources = [ChatCitationType.DocumentedInterests];

  if (citation.type === ChatCitationType.CallCenterInteractions) {
    const reactive = citation.snippets.some(snippet => isReactiveCallCenterInteraction(snippet.metadata as Chat.CallCenterInteractionMetadata));
    const proactive = citation.snippets.some(snippet => !isReactiveCallCenterInteraction(snippet.metadata as Chat.CallCenterInteractionMetadata));
    return [
      ...(reactive ? [EngagementType.Reactive] : []),
      ...(proactive ? [EngagementType.Proactive] : []),
    ];
  }
  if (citation.type === ChatCitationType.FbmsInteractions) {
    const reactive = citation.snippets.some(snippet => isReactiveMslInteraction(snippet.metadata as Chat.FBMSInteractionMetadata));
    const proactive = citation.snippets.some(snippet => !isReactiveMslInteraction(snippet.metadata as Chat.FBMSInteractionMetadata));
    return [
      ...(reactive ? [EngagementType.Reactive] : []),
      ...(proactive ? [EngagementType.Proactive] : []),
    ];
  }
  if (citation.type == ChatCitationType.CrmNote) {
    const metadata = citation.metadata as Chat.CrmNoteCitationMetadata;
    if (metadata.type === InteractionTypeEnum.DocumentedInterest) return [EngagementType.Reactive];
    if (metadata.type === InteractionTypeEnum.CallCenterInteraction) {
      return isReactiveCallCenterInteraction(metadata) ? [EngagementType.Reactive] : [EngagementType.Proactive];
    }
    if (metadata.type === InteractionTypeEnum.FbmsInteraction) {
      return isReactiveMslInteraction(metadata) ? [EngagementType.Reactive] : [EngagementType.Proactive];
    }
  }
  if (proactiveSources.includes(citation.type)) {
    return [EngagementType.Proactive];
  }
  if (reactiveSources.includes(citation.type)) {
    return [EngagementType.Reactive];
  }
  return [EngagementType.None];
}

export function isReactiveCallCenterInteraction(metadata: Chat.CallCenterInteractionMetadata) {
  return [
    'mir',
    'medical information request',
  ].includes(metadata?.interactionSubType?.toLowerCase());
}
export function isReactiveMslInteraction(metadata: Chat.FBMSInteractionMetadata) {
  return [
    'reactive',
  ].includes(metadata?.interactionSubType?.toLowerCase());
}

export function getEngagementTypeFromCitationType(citationType: ChatCitationType, mir: boolean): EngagementType {

  const proactiveSources = [ChatCitationType.ScientificLeaderTopic, ChatCitationType.ClinicalLeaderTopic,
  ChatCitationType.DigitalLeaderTopic, ChatCitationType.Association, ChatCitationType.Affiliation,
  ChatCitationType.Collaboration, ChatCitationType.CompanySponsorship, ChatCitationType.ClinicalTrial,
  ChatCitationType.Conference, ChatCitationType.Publication, ChatCitationType.CongressAbstract,
  ChatCitationType.ContactDetail, ChatCitationType.UnmetNeed, ChatCitationType.FbmsInteractions,
  ];

  const reactiveSources = [ChatCitationType.DocumentedInterests];

  if (citationType === ChatCitationType.CallCenterInteractions) {
    if (mir) {
      return EngagementType.Reactive;
    }
    else return EngagementType.Proactive;
  }
  if (proactiveSources.includes(citationType)) {
    return EngagementType.Proactive;
  }
  if (reactiveSources.includes(citationType)) {
    return EngagementType.Reactive;
  }
  return EngagementType.None;
}